import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy, RenderTask, TextLayer } from 'pdfjs-dist';

import { loadLibrary, pageRenderScale } from '@/lib/pdf/pdf';

import styles from './PdfViewer.module.css';

export interface PdfPageProps {
  doc: PDFDocumentProxy;
  pageNumber: number;
  /** CSS pixels the page is drawn across. */
  width: number;
  /** Height / width, held until the page has painted so nothing below it moves. */
  ratio: number;
  /** The scroller the page is judged visible against. */
  root: HTMLElement | null;
}

const MAX_PIXEL_RATIO = 2;
// Drawn a little ahead of the reader, so a page is usually ready by the time it arrives.
const ROOT_MARGIN = '400px';
const CANCELLED = ['RenderingCancelledException', 'AbortException'];

/**
 * One page as a canvas with its text laid over it, invisible and selectable.
 *
 * Drawn only while near the viewport and released once it leaves, as app-web's
 * stage does: a long document at a pixel ratio of 2 costs megabytes per page held.
 */
export function PdfPage({ doc, pageNumber, width, ratio, root }: PdfPageProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [hasPainted, setHasPainted] = useState(false);
  const painted = visible && hasPainted;

  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => setVisible(entries.some((entry) => entry.isIntersecting)),
      { root, rootMargin: ROOT_MARGIN },
    );
    observer.observe(host);
    return () => observer.disconnect();
  }, [root]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const text = textRef.current;
    if (!canvas || !text) {
      return;
    }
    if (!visible) {
      release(canvas, text);
      return;
    }
    if (!width) {
      return;
    }

    let cancelled = false;
    let task: RenderTask | undefined;
    let textLayer: TextLayer | undefined;

    const draw = async () => {
      const page = await doc.getPage(pageNumber);
      if (cancelled) {
        return;
      }
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: width / base.width });
      const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
      const bitmap = page.getViewport({
        scale: pageRenderScale(width, base.width, base.height, pixelRatio),
      });

      // Drawn aside and swapped in whole, so a resize never flashes the page white.
      const frame = document.createElement('canvas');
      frame.width = Math.floor(bitmap.width);
      frame.height = Math.floor(bitmap.height);
      const frameContext = frame.getContext('2d');
      const context = canvas.getContext('2d');
      if (!frameContext || !context) {
        return;
      }
      task = page.render({ canvas: frame, canvasContext: frameContext, viewport: bitmap });
      await task.promise;
      if (cancelled) {
        return;
      }
      canvas.width = frame.width;
      canvas.height = frame.height;
      canvas.style.aspectRatio = '';
      context.drawImage(frame, 0, 0);
      setHasPainted(true);

      const [{ TextLayer: Layer }, content] = await Promise.all([
        loadLibrary(),
        page.getTextContent(),
      ]);
      if (cancelled) {
        return;
      }
      const staging = document.createElement('div');
      textLayer = new Layer({ textContentSource: content, container: staging, viewport });
      await textLayer.render();
      if (cancelled) {
        return;
      }
      text.style.setProperty('--total-scale-factor', `${viewport.scale}`);
      text.replaceChildren(...staging.childNodes);
    };

    draw().catch((error: unknown) => {
      if (!cancelled && !CANCELLED.includes((error as Error)?.name)) {
        console.error(`Page ${pageNumber} could not be drawn.`, error);
      }
    });

    return () => {
      cancelled = true;
      task?.cancel();
      textLayer?.cancel();
    };
  }, [doc, pageNumber, width, visible]);

  return (
    <div
      aria-label={`Page ${pageNumber}`}
      className={`${styles.page} ${painted ? styles.painted : ''}`}
      data-page={pageNumber}
      ref={hostRef}
      role="region"
      style={{ width, ['--page-ratio' as string]: ratio }}
    >
      <canvas className={styles.canvas} ref={canvasRef} />
      <div className={styles.textLayer} ref={textRef} />
    </div>
  );
}

/** Frees the bitmap but keeps the page's shape, so nothing above the reader shifts. */
function release(canvas: HTMLCanvasElement, text: HTMLDivElement) {
  text.replaceChildren();
  if (!canvas.width || !canvas.hasAttribute('width')) {
    return;
  }
  canvas.style.aspectRatio = `${canvas.width} / ${canvas.height}`;
  canvas.width = 0;
  canvas.height = 0;
}
